import { envConfig } from '@/config/env';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generateUUID } from '@/utils/uuid';
import { RechargeStatus } from '@/types';

// WebSocket 关闭码枚举
export enum WS_CLOSE_CODE {
  NORMAL_CLOSURE = 1000, // 正常关闭（Normal Closure）- 不重连，面试正常结束
  GOING_AWAY = 1001, // 服务端主动关闭（Going Away）- 可重连，服务端维护或重启
  ABNORMAL_CLOSURE = 1006, // 异常关闭（Abnormal Closure）- 可重连，网络中断或超时
  POLICY_VIOLATION = 1008, // 策略违规（Policy Violation）- 不重连，检查认证或权限
  INTERNAL_ERROR = 1011, // 服务器错误（Internal Error）- 可重连，服务端内部错误
}

export type EventType = 'system' | 'message' | 'event';
export type MessageType =
  // | 'welcome_message'
  // | 'interview_question'
  // | 'interview_answer_ack'
  | 'interview_end'
  | 'session_init'
  | 'system_notification'
  | 'error'
  | 'ping'
  | 'pong'
  | 'message_ack'
  | 'questionnaire_push'
  | 'report_status'
  | 'recharge_result';
export type SessionStatus = 'new' | 'resume_wait_user' | 'resume_wait_question';
export type MessageStatus = 'thinking' | 'start' | 'delta' | 'end';

export interface Message {
  eventType: EventType;
  type: MessageType;
  timestamp: number;
  messageId: string;
}

export interface InitMessage extends Message {
  eventType: 'system';
  type: 'session_init';
  sessionId: string;
  sessionStatus: SessionStatus;
  interviewNumber: number;
}

// export interface WelcomeMessage extends Message {
//   eventType: 'message';
//   type: 'welcome_message';
//   role: 'assistant';
//   interviewNumber: number;
//   status: MessageStatus;
//   content: string;
// }

// export interface InterviewQuestionMessage extends Message {
//   eventType: 'message';
//   type: 'interview_question';
//   role: 'assistant';
//   interviewNumber: number;
//   status: MessageStatus;
//   content: string;
// }

// export interface InterviewAnswerAckMessage extends Message {
//   eventType: 'message';
//   type: 'interview_answer_ack';
//   role: 'user';
//   interviewNumber: number;
//   content: string;
// }

export interface InterviewEndMessage extends Message {
  eventType: 'system';
  type: 'interview_end';
  interviewNumber: number;
  duration: number;
}

export interface SystemNotificationMessage extends Message {
  eventType: 'system';
  type: 'system_notification';
  notificationId: number;
  title: string;
  content: string;
  url?: string;
  level: 'info' | 'warning' | 'error' | 'success';
}

export interface ReportStatusMessage extends Message {
  eventType: 'event';
  type: 'report_status';
  status: 'success' | 'too_few_rounds';
  title: string;
  interviewNumber: number;
  message: string;
  url?: string;
}

export interface QuestionnairePushMessage extends Message {
  eventType: 'event';
  type: 'questionnaire_push';
  timestamp: number;
  questionnaireNumber: number;
  title: string;
  description: string;
  rewardPoints: number;
  expireSeconds: number;
}

export interface RechargeResultMessage extends Message {
  eventType: 'event';
  type: 'recharge_result';
  orderNo: string;
  status: RechargeStatus;
  amountPoints: number;
  totalFee: number;
  message: string;
}

export interface ErrorMessage extends Message {
  eventType: 'system';
  type: 'error';
  code: 401 | 1000 | 500; // 认证失败 ｜ 参数无效 ｜ 服务器错误
  message: string;
}

// 下行消息
export type WSMessage =
  | InitMessage
  // | WelcomeMessage
  // | InterviewQuestionMessage
  // | InterviewAnswerAckMessage
  | InterviewEndMessage
  | SystemNotificationMessage
  | ErrorMessage
  | QuestionnairePushMessage
  | ReportStatusMessage
  | RechargeResultMessage;

// 上行消息
export interface AckMessage extends Message {
  eventType: 'message';
  type: 'message_ack';
}

export interface PingMessage extends Message {
  eventType: 'event';
  type: 'ping';
}

// 心跳配置
const HEARTBEAT_INTERVAL = 30000; // 30秒发送一次心跳
const HEARTBEAT_TIMEOUT = 60000; // 60秒未收到响应则认为超时

export interface WSConfig {
  onMessage?: (result: WSMessage) => void;
}

export interface UseWSReturn {
  isConnected: boolean;
  status: string;
  error: string | null;
  connect: () => Promise<void>;
  sendAck: (messageId: string) => void;
  destory: () => void;
}
/**
 * 面试聊天消息
 */
export function useWebSocket(config: WSConfig = {}): UseWSReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('未连接');
  const [error, setError] = useState<string | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const onMessageCallbackRef = useRef<((message: WSMessage) => void) | undefined>();
  const heartbeatTimerRef = useRef<number | null>(null);
  const heartbeatTimeoutTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const connectRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    onMessageCallbackRef.current = config.onMessage;
  }, [config.onMessage]);

  // 清理所有定时器
  const clearAllTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (heartbeatTimeoutTimerRef.current) {
      clearTimeout(heartbeatTimeoutTimerRef.current);
      heartbeatTimeoutTimerRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // 发送心跳
  const sendHeartbeat = useCallback(() => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const pingMessage: PingMessage = {
        eventType: 'event',
        type: 'ping',
        messageId: generateUUID(),
        timestamp: Date.now(),
      };
      websocketRef.current.send(JSON.stringify(pingMessage));

      // 设置超时检测
      if (heartbeatTimeoutTimerRef.current) {
        clearTimeout(heartbeatTimeoutTimerRef.current);
      }
      heartbeatTimeoutTimerRef.current = setTimeout(() => {
        // 心跳超时，关闭连接
        if (websocketRef.current) {
          websocketRef.current.close();
        }
      }, HEARTBEAT_TIMEOUT);
    }
  }, []);

  // 启动心跳
  const startHeartbeat = useCallback(() => {
    clearAllTimers();
    // 立即发送一次心跳
    sendHeartbeat();
    // 设置定期心跳
    heartbeatTimerRef.current = setInterval(() => {
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }, [sendHeartbeat, clearAllTimers]);

  // 处理心跳响应
  const handlePong = useCallback(() => {
    // 收到 pong，清除超时计时器
    if (heartbeatTimeoutTimerRef.current) {
      clearTimeout(heartbeatTimeoutTimerRef.current);
      heartbeatTimeoutTimerRef.current = null;
    }
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      clearAllTimers();
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, [clearAllTimers]);

  const connect = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      // 如果已经连接，直接返回
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // 清理之前的连接和定时器
      clearAllTimers();
      if (websocketRef.current && websocketRef.current.readyState !== WebSocket.CLOSED) {
        websocketRef.current.close();
      }

      // 使用环境配置的 WebSocket 基础 URL（全局连接，不包含面试号）
      const wsUrl = `${envConfig.wsBaseUrl}/api/ws/user?token=${window.config?.token}`;
      websocketRef.current = new WebSocket(wsUrl);

      websocketRef.current.onopen = () => {
        setIsConnected(true);
        setStatus('已连接');
        setError(null);
        // 连接成功后启动心跳
        startHeartbeat();
        resolve();
      };

      websocketRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data) as WSMessage;

        // 收到任何消息都重置心跳超时（表示连接活跃）
        handlePong();
        onMessageCallbackRef.current?.(message);
      };

      websocketRef.current.onerror = (event) => {
        const errMsg = event instanceof Error ? event.message : 'WebSocket 连接错误';
        setError(errMsg);
        setStatus(errMsg);
        clearAllTimers();
        reject(errMsg);
      };

      websocketRef.current.onclose = (event) => {
        setIsConnected(false);
        setStatus('未连接');
        clearAllTimers();

        console.warn(`ws 收到关闭事件： ${event.code} ${event.reason}`);
        // 如果不是主动关闭，尝试重连
        // 根据 WS_CLOSE_CODE 枚举进行重连判断
        if (
          [
            WS_CLOSE_CODE.GOING_AWAY,
            WS_CLOSE_CODE.ABNORMAL_CLOSURE,
            WS_CLOSE_CODE.INTERNAL_ERROR,
          ].includes(event.code) &&
          connectRef.current
        ) {
          reconnectTimerRef.current = setTimeout(() => {
            connectRef.current?.().catch(() => {
              // WebSocket 重连失败
            });
          }, 3000); // 3秒后重连
        }
      };
    });
  }, [clearAllTimers, startHeartbeat, handlePong]);

  // 保存 connect 函数引用，用于重连
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const sendAck = useCallback((messageId: string) => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      websocketRef.current.send(
        JSON.stringify({
          type: 'message_ack',
          messageId,
        }),
      );
    }
  }, []);

  const destory = useCallback(() => {
    clearAllTimers();
    if (websocketRef.current) {
      websocketRef.current.close(1000, '用户主动断开连接');
      websocketRef.current = null;
    }
    setIsConnected(false);
    setStatus('未连接');
  }, [clearAllTimers]);

  return {
    isConnected,
    error,
    status,
    connect,
    sendAck,
    destory,
  };
}
